import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { INSIGHTS_FIELD, Linqset } from "../../common/linqset-model";
import { InsightsFieldType } from "../../insights/model/insights-field-type";
import { NodeTypeData } from "../node-types/node-type-data";
import { PARETO_FIELDS } from "./node-chart-data";

export enum CHART_TYPE {
    BAR,
    BUBBLE,
    CAPACITY,       // how loaded each role or person is
    PARETO,         // the few that make up most of an attribute
    HEATMAP,        // nodes against the models they work in
    COMPARE         // each node before and after, against the workspace compared with
}

@Component({
    selector: 'app-model-charts',
    templateUrl: './model-charts.html',
    styleUrls: ['./model-charts.css'],
    standalone: false
})
export class ModelChartsComponent implements OnChanges {
    @Input() linqset: Linqset
    @Input() comparison: Linqset
    @Input() selectedTypeData:NodeTypeData
    @Input() includeCarbon:boolean
    @Input() trigger:any
    @Input() chartType:CHART_TYPE
    
    @Output() selectNodeEvent = new EventEmitter<string>();

    childTrigger:any    // triggers updates in child components
    fieldTypes:InsightsFieldType[] = []

    topNumber:number
    hideZeros:boolean
    
    CHART_TYPE=CHART_TYPE
    
    constructor() {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.linqset && (changes["linqset"] || changes["chartType"])) {
            this.selectChartType(this.chartType)
            this.childTrigger = {}
        }
    }

    getChartFields() {
        return Object.values(INSIGHTS_FIELD).filter(f => {
            const ft = new InsightsFieldType(f, this.linqset.modelKey, this.linqset.workspaceKey, this.comparison?.workspaceKey)
            return ft.doesFieldTypeApplyForTypeData(this.selectedTypeData) && ((this.chartType !== CHART_TYPE.PARETO && this.chartType !== CHART_TYPE.HEATMAP) || PARETO_FIELDS.includes(f))
        })
    }

    selectChartType(type:CHART_TYPE) {
        if (!this.linqset) return

        if (type === CHART_TYPE.BUBBLE) {
            this.fieldTypes = [
                new InsightsFieldType(INSIGHTS_FIELD.NONE, this.linqset.modelKey, this.linqset.workspaceKey, this.comparison?.workspaceKey),
                new InsightsFieldType(INSIGHTS_FIELD.NONE, this.linqset.modelKey, this.linqset.workspaceKey, this.comparison?.workspaceKey),
                new InsightsFieldType(INSIGHTS_FIELD.NONE, this.linqset.modelKey, this.linqset.workspaceKey, this.comparison?.workspaceKey)
            ]
        }else if (type === CHART_TYPE.CAPACITY) {
            this.fieldTypes = []
        }else {
            this.fieldTypes = [
                new InsightsFieldType(INSIGHTS_FIELD.NONE, this.linqset.modelKey, this.linqset.workspaceKey, this.comparison?.workspaceKey)
            ]
        }
    }

    getLabel(i:number) {
        if (this.chartType !== CHART_TYPE.BUBBLE) return "Attribute"
        else if (i === 0) return 'Across (x)'
        else if (i === 1) return 'Up (y)'
        else return 'Bubble size'
    }

    typeName() {
        return (this.selectedTypeData?.name || '').toLowerCase()
    }

    // what the chart shows, in a line
    title() {
        const name = this.selectedTypeData?.name || 'Nodes'
        if (this.chartType === CHART_TYPE.BUBBLE) return `${name}: ${this.fieldTypes[0]?.type} against ${this.fieldTypes[1]?.type}`
        if (this.chartType === CHART_TYPE.CAPACITY) return `${name}: work against capacity`
        if (this.chartType === CHART_TYPE.PARETO) return `${name}: where most of the ${String(this.fieldTypes[0]?.type).toLowerCase()} is`
        if (this.chartType === CHART_TYPE.HEATMAP) return `${name}: ${String(this.fieldTypes[0]?.type).toLowerCase()} by model`
        if (this.chartType === CHART_TYPE.COMPARE) return `${name}: ${String(this.fieldTypes[0]?.type).toLowerCase()} before and after`
        const top = this.topNumber || 35
        return `${name}: the top ${top} by ${this.fieldTypes[0]?.type}`
    }

    subtitle() {
        const totals = this.selectedTypeData?.usesTotals && this.fieldTypes[0]?.outcomeApplies ? ' Values are the total cumulative values for each outcome.' : ''
        if (this.chartType === CHART_TYPE.BUBBLE) return `Each bubble is a node, sized by ${this.fieldTypes[2]?.type}. Click one to select it.${totals}`
        if (this.chartType === CHART_TYPE.CAPACITY) return 'The hours of work in a year as a share of the hours each has, the most loaded first. The dashed line is 100% of capacity. Click a bar to select the node.'
        if (this.chartType === CHART_TYPE.HEATMAP) return 'Each row is a node and each column a model, the darker the more. The largest nodes and the busiest models come first. Hover over a cell for its value.'
        if (this.chartType === CHART_TYPE.COMPARE) return `What each node has in current state and in the project, joined by a line.${totals}`
        if (this.chartType === CHART_TYPE.PARETO) return `Largest first. The dashed line is where the few that make up 80% of the total end. Click a bar to select the node.${totals}`
        return `Largest first${this.hideZeros ? ', without zeros' : ''}. Click a bar to select the node.${totals}`
    }

    selectType(type:NodeTypeData) {
        this.selectedTypeData = type

        this.fieldTypes.forEach(ft => {
            if (!ft.doesFieldTypeApplyForTypeData(this.selectedTypeData))
                ft.type = INSIGHTS_FIELD.NONE
        })
    }

    selectField(event) {
        this.childTrigger = {}
    }

    isValid() {
        if (this.chartType === CHART_TYPE.CAPACITY) return true
        if (this.fieldTypes.length === 0) return false
        return this.chartType !== CHART_TYPE.BUBBLE?this.fieldTypes[0].isValid():this.fieldTypes[0].isValid()&&this.fieldTypes[1].isValid()&&this.fieldTypes[2].isValid()
    }

    selectNodeForKey(key:string) {
        this.selectNodeEvent.emit(key)
    }

}